import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardMedia,
  Grid,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Badge,
  InputBase,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  ThemeProvider,
  createTheme,
  alpha,
  useScrollTrigger,
  Fade,
  Chip,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  IconButton as MuiIconButton,
  Menu,
  MenuItem,
  Snackbar
} from '@mui/material';
import { Pagination, FormControl, InputLabel, Select } from '@mui/material';
import {
  Search as SearchIcon,
  ShoppingCart as ShoppingCartIcon,
  Menu as MenuIcon,
  Person as PersonIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Close as CloseIcon,
  AddShoppingCart as AddShoppingCartIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  Remove as RemoveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import API_CONFIG from '../../config/api.config';
import customerAuthService from '../../services/customerAuthService';
import ecommerceOrderService from '../../services/ecommerceOrderService';
import CustomerAuthDialog from '../../components/ecommerce/CustomerAuthDialog';
import useCustomerSessionTimeout from '../../hooks/useCustomerSessionTimeout';
import { formatCurrency } from '../../utils/currencyFormatter';

// --- Custom Components ---

// Elevation Scroll for Navbar
function ElevationScroll(props) {
  const { children } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  return React.cloneElement(children, {
    elevation: trigger ? 4 : 0,
    sx: {
      ...children.props.sx,
      bgcolor: trigger ? 'background.paper' : 'transparent',
      color: trigger ? 'text.primary' : 'white',
      transition: 'all 0.3s ease',
      borderBottom: trigger ? 1 : 0,
      borderColor: trigger ? 'divider' : 'transparent',
      '& .MuiToolbar-root': {
        color: trigger ? 'text.primary' : 'white',
      },
      '& .MuiInputBase-root': {
        color: trigger ? 'text.primary' : 'white',
        '&::placeholder': {
          color: trigger ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.7)'
        }
      },
      '& .MuiIconButton-root': {
        color: trigger ? 'text.primary' : 'white',
      },
      '& .MuiButton-root': {
        color: trigger ? 'text.primary' : 'white',
      }
    }
  });
}

// Helper to get API origin (protocol + host + optional port), ignoring path
const getApiOrigin = () => {
  try {
    const u = new URL(API_CONFIG.BASE_URL);
    return u.origin;
  } catch {
    const a = document.createElement('a');
    a.href = API_CONFIG.BASE_URL;
    return `${a.protocol}//${a.host}`;
  }
};

// Helper function to get full product image URL
const getProductImageUrl = (imageUrl) => {
  if (!imageUrl) return 'https://via.placeholder.com/300x300?text=No+Image';
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  const origin = getApiOrigin();
  const path = imageUrl.startsWith('/api') ? imageUrl : `/api${imageUrl.startsWith('/') ? imageUrl : '/' + imageUrl}`;
  return `${origin}${path}`;
};

// Product Card Component
const ProductCard = ({ product, onAddToCart }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Card
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
        },
        borderRadius: 3,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Box sx={{ position: 'relative', pt: '100%', overflow: 'hidden', bgcolor: '#f5f5f7' }}>
        <CardMedia
          component="img"
          image={getProductImageUrl(product.imageUrl)}
          alt={product.name}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain', // Use contain to show full product clearly
            p: 2,
            transition: 'transform 0.5s ease',
            transform: isHovered ? 'scale(1.05)' : 'scale(1)'
          }}
        />
        {!product.inStock && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              bgcolor: 'rgba(255,255,255,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1
            }}
          >
            <Chip label="Out of Stock" color="error" sx={{ fontWeight: 'bold' }} />
          </Box>
        )}
      </Box>
      
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: { xs: 0.5, md: 1 }, p: { xs: 1.5, md: 2 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' }, textTransform: 'uppercase', letterSpacing: 1 }}>
          {product.categoryName || 'General'}
        </Typography>
        <Typography 
          variant="h6" 
          component="h3" 
          sx={{ 
            fontWeight: 600, 
            lineHeight: 1.2, 
            flexGrow: 1,
            fontSize: { xs: '0.9rem', md: '1.25rem' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {product.name}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mt: { xs: 0.5, md: 1 } }}>
          <Box sx={{ flex: 1 }}>
             {product.mrp > product.price && (
              <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through', fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
                {formatCurrency(product.mrp)}
              </Typography>
            )}
            <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', md: '1.25rem' } }}>
              {formatCurrency(product.price)}
            </Typography>
          </Box>
          
          <IconButton 
            color="primary" 
            disabled={!product.inStock}
            onClick={() => onAddToCart(product)}
            sx={{ 
              bgcolor: 'primary.main', 
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
              transform: isHovered ? 'scale(1)' : 'scale(0.9)',
              opacity: isHovered ? 1 : 0.8,
              transition: 'all 0.2s ease',
              width: { xs: 32, md: 40 },
              height: { xs: 32, md: 40 },
              '& .MuiSvgIcon-root': {
                fontSize: { xs: '1rem', md: '1.25rem' }
              }
            }}
          >
            <AddShoppingCartIcon />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

const StorefrontTestPage = () => {
  const { websitePath } = useParams();
  const navigate = useNavigate();
  const cartStorageKey = useMemo(() => `ecommerce_cart_${websitePath}`, [websitePath]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [websiteData, setWebsiteData] = useState(null);
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({ page: 0, size: 12, totalPages: 0, totalItems: 0 });
  const [cartOpen, setCartOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [infoDialogType, setInfoDialogType] = useState(null); // 'about', 'shipping', 'returns'
  const productsSectionRef = useRef(null);

  const filteredProducts = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => {
      const name = (p.name || '').toLowerCase();
      const category = (p.categoryName || '').toLowerCase();
      return name.includes(q) || category.includes(q);
    });
  }, [products, searchQuery]);

  // Customer Auth State
  const [customer, setCustomer] = useState(null);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState(null);

  // Cart State
  const [cart, setCart] = useState([]);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null); // { orderNumber, total }
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Session timeout tracking
  useCustomerSessionTimeout();

  // Check for logged in customer on mount
  useEffect(() => {
    const currentCustomer = customerAuthService.getCurrentCustomer();
    if (currentCustomer) {
      setCustomer(currentCustomer);
    } else {
      // If session expired, customer will be null
      setCustomer(null);
    }
  }, []);

  // Load cart from localStorage on mount / website change
  const loadCart = () => {
    try {
      const savedCart = localStorage.getItem(cartStorageKey);
      if (savedCart) {
        const parsed = JSON.parse(savedCart);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setCart(prevCart => {
            // Merge with existing cart to preserve any items already in state
            if (prevCart.length === 0) {
              console.log('[StorefrontTestPage] Loading cart from storage:', parsed.length, 'items');
              return parsed;
            }
            // Merge: combine items, keeping the most recent version
            const merged = [...prevCart];
            parsed.forEach(storageItem => {
              const existingIndex = merged.findIndex(item => item.productId === storageItem.productId);
              if (existingIndex >= 0) {
                // Update existing item
                merged[existingIndex] = storageItem;
              } else {
                // Add new item from storage
                merged.push(storageItem);
              }
            });
            console.log('[StorefrontTestPage] Merged cart:', merged.length, 'items (prev:', prevCart.length, 'storage:', parsed.length, ')');
            return merged;
          });
        }
      }
    } catch (e) {
      console.error('[StorefrontTestPage] Failed to load cart from storage', e);
    }
  };

  useEffect(() => {
    loadCart();
    // Reload cart when page comes into focus
    const handleFocus = () => {
      loadCart();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [cartStorageKey]);

  // Persist cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(cartStorageKey, JSON.stringify(cart));
      console.log('[StorefrontTestPage] Saved cart to storage:', cart.length, 'items', cartStorageKey);
    } catch (e) {
      console.error('[StorefrontTestPage] Failed to save cart to storage', e);
    }
  }, [cart, cartStorageKey]);

  const handleLoginSuccess = (customerData) => {
    setCustomer(customerData);
  };

  const handleLogout = () => {
    customerAuthService.logout();
    setCustomer(null);
    setAnchorElUser(null);
    setCartOpen(false); // Close cart if open
    setCart([]); // Clear cart on logout
    try {
      localStorage.removeItem(cartStorageKey);
    } catch (e) {
      console.error('Failed to clear cart storage on logout', e);
    }
  };

  // Cart Functions
  const addToCart = (product) => {
    setCart(prevCart => {
      // First, ensure we have the latest cart from localStorage
      try {
        const savedCart = localStorage.getItem(cartStorageKey);
        if (savedCart) {
          const parsed = JSON.parse(savedCart);
          if (Array.isArray(parsed) && parsed.length > 0) {
            // Merge saved cart with current state
            const merged = [...parsed];
            const existingItem = merged.find(item => item.productId === product.id);
            if (existingItem) {
              // Update quantity if item already in cart
              const updated = merged.map(item =>
                item.productId === product.id
                  ? { ...item, quantity: item.quantity + 1 }
                  : item
              );
              console.log('[StorefrontTestPage] Updated existing product in cart:', product.name);
              return updated;
            } else {
              // Add new item to cart
              const newCart = [...merged, {
                productId: product.id,
                productName: product.name,
                productImage: product.imageUrl,
                unitPrice: product.price,
                quantity: 1,
                inStock: product.inStock
              }];
              console.log('[StorefrontTestPage] Added new product to cart:', product.name, 'Total items:', newCart.length);
              return newCart;
            }
          }
        }
      } catch (e) {
        console.error('[StorefrontTestPage] Error reading cart from storage in addToCart:', e);
      }
      
      // Fallback: use prevCart if localStorage read fails
      const existingItem = prevCart.find(item => item.productId === product.id);
      if (existingItem) {
        // Update quantity if item already in cart
        return prevCart.map(item =>
          item.productId === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        // Add new item to cart
        return [...prevCart, {
          productId: product.id,
          productName: product.name,
          productImage: product.imageUrl,
          unitPrice: product.price,
          quantity: 1,
          inStock: product.inStock
        }];
      }
    });
    
    // Show notification that product was added to cart
    setSnackbarMessage('Product added to cart');
    setSnackbarOpen(true);
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.productId !== productId));
  };

  const updateCartQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prevCart =>
      prevCart.map(item =>
        item.productId === productId
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.unitPrice * item.quantity), 0);
  };

  const getCartItemCount = () => {
    // Return the number of unique products in cart, not total quantity
    return cart.length;
  };

  // Place Order
  const handlePlaceOrder = async () => {
    if (!customer) {
      // Ask customer to login first
      setAuthDialogOpen(true);
      return;
    }
    if (cart.length === 0) {
      return;
    }
    // Navigate to dedicated cart page for review & checkout
    setCartOpen(false);
    navigate(`/${websitePath}/cart`);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  // Fetch Company Info
  useEffect(() => {
    const fetchWebsiteData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(`${API_CONFIG.BASE_URL}/public/website/${websitePath}`);
        
        if (!response.ok) {
          throw new Error(response.status === 404 ? 'Website not found' : 'Failed to load website');
        }
        
        const data = await response.json();
        if (data.success) {
          setWebsiteData(data);
        } else {
          throw new Error(data.message || 'Failed to load website data');
        }
      } catch (err) {
        console.error('Error fetching website data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (websitePath) fetchWebsiteData();
  }, [websitePath]);

  // Fetch Products
  useEffect(() => {
    const fetchProducts = async () => {
      if (!websiteData) return;
      
      try {
        setProductsLoading(true);
        const response = await fetch(
          `${API_CONFIG.BASE_URL}/public/website/${websitePath}/products?page=${pagination.page}&size=${pagination.size}${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`
        );
        
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setProducts(data.products || []);
            setPagination(prev => ({
              ...prev,
              totalPages: data.totalPages || 0,
              totalItems: data.totalItems || 0
            }));
          }
        }
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProducts();
  }, [websiteData, websitePath, pagination.page, pagination.size, searchQuery]);

  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 0 }));
  }, [searchQuery]);

  // Create Custom Theme based on company settings (or defaults)
  const theme = useMemo(() => {
    // Default "Premium" Colors
    const defaultPrimary = '#1a237e'; // Midnight Blue
    const defaultSecondary = '#c51162'; // Pink accent

    // Use company colors from website settings if available
    const primaryColor = websiteData?.primaryColor || defaultPrimary;
    const secondaryColor = websiteData?.secondaryColor || defaultSecondary;
    
    return createTheme({
      palette: {
        primary: {
          main: primaryColor,
        },
        secondary: {
          main: secondaryColor,
        },
        background: {
          default: '#f8f9fa',
          paper: '#ffffff',
        },
        text: {
          primary: '#2d3436',
          secondary: '#636e72',
        }
      },
      typography: {
        fontFamily: '"Plus Jakarta Sans", "Inter", "Roboto", sans-serif', // Modern fonts
        h1: { fontWeight: 700 },
        h2: { fontWeight: 700 },
        h3: { fontWeight: 600 },
        h4: { fontWeight: 600 },
        h5: { fontWeight: 600 },
        h6: { fontWeight: 600 },
        button: { textTransform: 'none', fontWeight: 600 }
      },
      shape: {
        borderRadius: 12
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 8,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }
            }
          }
        },
        MuiPaper: {
          styleOverrides: {
            elevation1: {
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
            }
          }
        }
      }
    });
  }, [websiteData]);

  const getLogoUrl = (logoUrl) => {
    if (!logoUrl) return '';
    if (logoUrl.startsWith('http')) return logoUrl;
    const origin = getApiOrigin();
    const path = logoUrl.startsWith('/api') ? logoUrl : `/api/${logoUrl}`;
    return `${origin}${path}`;
  };

  // Scroll to products section
  const scrollToProducts = () => {
    if (productsSectionRef.current) {
      productsSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  
  const handlePageChange = (event, value) => {
    setPagination(prev => ({ ...prev, page: value - 1 }));
    scrollToProducts();
  };
  
  const handleRowsPerPageChange = (event) => {
    const newSize = Number(event.target.value);
    setPagination(prev => ({ ...prev, size: newSize, page: 0 }));
    scrollToProducts();
  };

  // Open info dialog
  const handleOpenInfoDialog = (type) => {
    setInfoDialogType(type);
    setInfoDialogOpen(true);
  };

  // Close info dialog
  const handleCloseInfoDialog = () => {
    setInfoDialogOpen(false);
    setInfoDialogType(null);
  };

  // Get dialog content based on type
  const getDialogContent = () => {
    switch (infoDialogType) {
      case 'about':
        return {
          title: 'About Us',
          content: (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                Welcome to {websiteData?.companyName || 'Our Store'}
              </Typography>
              {websiteData?.aboutText ? (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    whiteSpace: 'pre-line',
                    lineHeight: 1.8
                  }}
                >
                  {websiteData.aboutText}
                </Typography>
              ) : (
                <Typography variant="body1" paragraph color="text.secondary">
                  Information about our company will be available soon.
                </Typography>
              )}
            </Box>
          )
        };
      case 'shipping':
        return {
          title: 'Shipping Policy',
          content: (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                Shipping Information
              </Typography>
              {websiteData?.shippingPolicy ? (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    whiteSpace: 'pre-line',
                    lineHeight: 1.8
                  }}
                >
                  {websiteData.shippingPolicy}
                </Typography>
              ) : (
                <Typography variant="body1" paragraph color="text.secondary">
                  Shipping policy information will be available soon.
                </Typography>
              )}
            </Box>
          )
        };
      case 'returns':
        return {
          title: 'Returns & Exchanges',
          content: (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                Return and Exchange Policy
              </Typography>
              {websiteData?.returnsPolicy ? (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    whiteSpace: 'pre-line',
                    lineHeight: 1.8
                  }}
                >
                  {websiteData.returnsPolicy}
                </Typography>
              ) : (
                <Typography variant="body1" paragraph color="text.secondary">
                  Returns and exchanges policy information will be available soon.
                </Typography>
              )}
            </Box>
          )
        };
      default:
        return { title: 'Information', content: <Typography>No information available.</Typography> };
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f8f9fa' }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8f9fa' }}>
        <Paper sx={{ p: 5, textAlign: 'center', maxWidth: 500, borderRadius: 4 }}>
          <Typography variant="h4" gutterBottom color="error.main" fontWeight="bold">Oops!</Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {error}
          </Typography>
          <Button variant="outlined" onClick={() => navigate('/', { replace: true })}>Return to Main Site</Button>
        </Paper>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
        
        {/* Navbar - Integrated with Hero */}
        <Box sx={{ position: 'relative' }}>
          <ElevationScroll>
            <AppBar 
              position="absolute" 
              sx={{ 
                bgcolor: 'transparent', 
                boxShadow: 'none',
                color: 'white'
              }}
            >
              <Container maxWidth="xl">
                <Toolbar sx={{ py: 2, gap: 2, justifyContent: 'space-between' }}>
                  {/* Logo */}
                  <Box component={RouterLink} to={`/${websitePath}`} sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit', gap: 2 }}>
                     {websiteData.logoUrl ? (
                      <Avatar
                        src={getLogoUrl(websiteData.logoUrl)}
                        sx={{ width: { xs: 40, md: 48 }, height: { xs: 40, md: 48 }, borderRadius: 2 }}
                        variant="rounded"
                      />
                    ) : (
                      <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2 }}>
                        <BusinessIcon />
                      </Avatar>
                    )}
                    <Typography 
                      variant="h6" 
                      component="div" 
                      sx={{ 
                        display: { xs: 'none', sm: 'block' }, 
                        fontWeight: 700, 
                        letterSpacing: '-0.5px',
                        color: 'white'
                      }}
                    >
                      {websiteData.companyName}
                    </Typography>
                  </Box>

                  

                  {/* Actions */}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <IconButton sx={{ display: { md: 'none' }, color: 'white' }}><SearchIcon /></IconButton>
                    
                    {customer ? (
                      <>
                        {/* Mobile profile icon */}
                        <IconButton
                          onClick={handleOpenUserMenu}
                          sx={{ display: { xs: 'flex', md: 'none' }, color: 'white' }}
                        >
                          <AccountCircleIcon />
                        </IconButton>
                        {/* Desktop profile button */}
                        <Button 
                          onClick={handleOpenUserMenu}
                          sx={{ 
                            display: { xs: 'none', md: 'flex' },
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                            textTransform: 'none',
                            fontWeight: 600
                          }}
                          startIcon={<AccountCircleIcon />}
                          endIcon={<Typography variant="caption" sx={{ ml: 0.5 }}>▼</Typography>}
                        >
                          {customer.name.split(' ')[0]}
                        </Button>
                        <Menu
                          sx={{ mt: '45px' }}
                          id="menu-appbar"
                          anchorEl={anchorElUser}
                          anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                          }}
                          keepMounted
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                          }}
                          open={Boolean(anchorElUser)}
                          onClose={handleCloseUserMenu}
                        >
                          <MenuItem 
                            component={RouterLink}
                            to={`/${websitePath}/profile`}
                            onClick={handleCloseUserMenu}
                          >
                            <Typography textAlign="center">Profile</Typography>
                          </MenuItem>
                          <MenuItem 
                            component={RouterLink}
                            to={`/${websitePath}/orders`}
                            onClick={handleCloseUserMenu}
                          >
                            <Typography textAlign="center">Orders</Typography>
                          </MenuItem>
                          <Divider />
                          <MenuItem onClick={handleLogout}>
                            <Typography textAlign="center" color="error.main">Logout</Typography>
                          </MenuItem>
                        </Menu>
                      </>
                    ) : (
                      <>
                        {/* Mobile login icon */}
                        <IconButton
                          sx={{ display: { xs: 'flex', md: 'none' }, color: 'white' }}
                          onClick={() => setAuthDialogOpen(true)}
                        >
                          <PersonIcon />
                        </IconButton>
                        {/* Desktop login button */}
                        <Button 
                          sx={{ 
                            display: { xs: 'none', md: 'flex' },
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                          }}
                          startIcon={<PersonIcon />}
                          onClick={() => setAuthDialogOpen(true)}
                        >
                          Login
                        </Button>
                      </>
                    )}

                    <IconButton 
                      sx={{ 
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                      }} 
                      onClick={() => navigate(`/${websitePath}/cart`)}
                    >
                      <Badge badgeContent={getCartItemCount()} color="secondary">
                        <ShoppingCartIcon />
                      </Badge>
                    </IconButton>
                  </Box>
                </Toolbar>
              </Container>
            </AppBar>
          </ElevationScroll>

          {/* Hero Section - Integrated with Navbar */}
          <Box 
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'white', 
            pt: { xs: 12, md: 16 }, 
            pb: { xs: 8, md: 12 },
            backgroundImage: websiteData?.primaryColor && websiteData?.secondaryColor
              ? `linear-gradient(135deg, ${websiteData.primaryColor} 0%, ${websiteData.secondaryColor} 100%)`
              : 'linear-gradient(135deg, rgba(26, 35, 126, 0.95) 0%, rgba(197, 17, 98, 0.85) 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
              pointerEvents: 'none'
            }
          }}
        >
          <Container maxWidth="lg" sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <Fade in timeout={1000}>
              <Box>
                <Typography 
                  variant="h2" 
                  component="h1" 
                  sx={{ 
                    mb: 2, 
                    fontWeight: 800, 
                    letterSpacing: '-1px',
                    color: 'white',
                    textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                    fontSize: { xs: '2rem', md: '3.5rem' }
                  }}
                >
                  {websiteData?.title || `Welcome to ${websiteData?.companyName || 'Our Store'}`}
                </Typography>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    mb: 4, 
                    color: 'rgba(255,255,255,0.95)',
                    fontWeight: 400, 
                    maxWidth: 800, 
                    mx: 'auto',
                    fontSize: { xs: '1rem', md: '1.5rem' },
                    textShadow: '0 1px 5px rgba(0,0,0,0.2)'
                  }}
                >
                  {websiteData?.tagline || 'Discover our premium collection of products selected just for you.'}
                </Typography>
                <Button 
                  variant="contained" 
                  color="secondary" 
                  size="large" 
                  onClick={scrollToProducts}
                  sx={{ 
                    px: { xs: 3, md: 4 }, 
                    py: { xs: 1.2, md: 1.5 }, 
                    fontSize: { xs: '0.9rem', md: '1.1rem' }, 
                    borderRadius: 50,
                    boxShadow: '0 4px 20px rgba(197, 17, 98, 0.4)',
                    '&:hover': {
                      boxShadow: '0 6px 25px rgba(197, 17, 98, 0.6)',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                  endIcon={<NavigateNextIcon />}
                >
                  Shop Now
                </Button>
              </Box>
            </Fade>
          </Container>
          </Box>
        </Box>

        {/* Products Section */}
        <Container maxWidth="xl" sx={{ mb: 8, flexGrow: 1 }} ref={productsSectionRef}>
          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h4" component="h2" fontWeight={700}>
              Featured Products
            </Typography>
             {/* Categories or Filter chips could go here */}
          </Box>
          
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3
          }}>
            <Box sx={{
              width: '100%',
              maxWidth: 600,
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1,
              borderRadius: 100,
              border: '1px solid',
              borderColor: 'grey.300',
              bgcolor: 'background.paper'
            }}>
              <SearchIcon sx={{ color: 'text.secondary' }} />
              <InputBase
                placeholder="Search for products..."
                sx={{ ml: 1, flex: 1 }}
                value={searchQuery}
                onChange={(e) => {
                  const v = e.target.value;
                  setSearchQuery(v);
                  setPagination(prev => ({ ...prev, page: 0 }));
                }}
              />
            </Box>
          </Box>

          {productsLoading ? (
            <Grid container spacing={{ xs: 2, sm: 3, md: 3 }}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                <Grid item xs={6} sm={6} md={3} lg={3} key={n}>
                  <Card sx={{ height: { xs: 320, md: 400 } }}>
                    <Box sx={{ height: { xs: 180, md: 250 }, bgcolor: 'grey.100' }} />
                    <CardContent>
                       <Box sx={{ height: 20, width: '80%', bgcolor: 'grey.200', mb: 1 }} />
                       <Box sx={{ height: 20, width: '40%', bgcolor: 'grey.200' }} />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : filteredProducts.length > 0 ? (
            <Grid container spacing={{ xs: 2, sm: 3, md: 3 }}>
              {filteredProducts.map((product) => (
                <Grid item xs={6} sm={6} md={3} lg={3} key={product.id}>
                  <ProductCard 
                    product={product} 
                    onAddToCart={(p) => {
                      if (!customer) {
                        setAuthDialogOpen(true);
                      } else {
                        addToCart(p);
                      }
                    }} 
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white', borderRadius: 4 }}>
              <Typography variant="h6" color="text.secondary">
                {searchQuery ? 'No products match your search.' : 'No products found in this store yet.'}
              </Typography>
            </Box>
          )}
          
          {!productsLoading && products.length > 0 && (
            <Box sx={{ mt: 3, position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Pagination
                count={Math.max(1, pagination.totalPages || 1)}
                page={(pagination.page || 0) + 1}
                onChange={handlePageChange}
                color="primary"
                sx={{ mx: 'auto', '& .MuiPaginationItem-root': { fontWeight: 700 } }}
              />
              <FormControl size="small" sx={{ minWidth: 140, position: 'absolute', right: 0 }}>
                <InputLabel id="rows-per-page-label">Per Page</InputLabel>
                <Select
                  labelId="rows-per-page-label"
                  id="rows-per-page-select"
                  value={pagination.size}
                  label="Per Page"
                  onChange={handleRowsPerPageChange}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </Container>

        {/* Footer */}
        <Box sx={{ bgcolor: 'grey.900', color: 'grey.300', py: 6, mt: 'auto' }}>
          <Container maxWidth="lg">
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Typography variant="h6" color="white" gutterBottom fontWeight="bold">
                  {websiteData.companyName}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, maxWidth: 300 }}>
                  {websiteData.addressLine1}, {websiteData.addressLine2}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}><FacebookIcon /></IconButton>
                  <IconButton size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}><TwitterIcon /></IconButton>
                  <IconButton size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}><InstagramIcon /></IconButton>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h6" color="white" gutterBottom fontWeight="bold">
                  Contact Us
                </Typography>
                <List dense disablePadding>
                  <ListItem disableGutters>
                    <ListItemAvatar><Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.1)' }}><PhoneIcon sx={{ fontSize: 18 }} /></Avatar></ListItemAvatar>
                    <ListItemText primary={websiteData.contactNumber || 'No contact info'} />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemAvatar><Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.1)' }}><EmailIcon sx={{ fontSize: 18 }} /></Avatar></ListItemAvatar>
                    <ListItemText primary={websiteData.email || 'No email'} />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={4}>
                 <Typography variant="h6" color="white" gutterBottom fontWeight="bold">
                  Information
                </Typography>
                 <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Link 
                      component="button" 
                      variant="body2" 
                      onClick={() => handleOpenInfoDialog('about')}
                      sx={{ 
                        color: 'inherit', 
                        textAlign: 'left',
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      About Us
                    </Link>
                    <Link 
                      component="button" 
                      variant="body2" 
                      onClick={() => handleOpenInfoDialog('shipping')}
                      sx={{ 
                        color: 'inherit', 
                        textAlign: 'left',
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      Shipping Policy
                    </Link>
                    <Link 
                      component="button" 
                      variant="body2" 
                      onClick={() => handleOpenInfoDialog('returns')}
                      sx={{ 
                        color: 'inherit', 
                        textAlign: 'left',
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      Returns & Exchanges
                    </Link>
                 </Box>
              </Grid>
            </Grid>
            <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />
            <Typography variant="body2" align="center" sx={{ opacity: 0.6 }}>
              © {new Date().getFullYear()} {websiteData.companyName}. All rights reserved.
            </Typography>
          </Container>
        </Box>

        {/* Cart Drawer */}
        <Drawer anchor="right" open={cartOpen} onClose={() => setCartOpen(false)}>
          <Box sx={{ width: { xs: '100%', sm: 400 }, p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">Shopping Cart</Typography>
              <IconButton onClick={() => setCartOpen(false)}><CloseIcon /></IconButton>
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            {cart.length === 0 ? (
              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
                <ShoppingCartIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.3 }} />
                <Typography color="text.secondary" variant="h6">Your cart is empty</Typography>
                <Typography color="text.secondary" variant="body2">Add some products to get started!</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }}>
                  <List disablePadding>
                    {cart.map((item) => (
                      <React.Fragment key={item.productId}>
                        <ListItem
                          sx={{
                            px: 0,
                            py: 2,
                            alignItems: 'flex-start'
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              variant="rounded"
                              src={getProductImageUrl(item.productImage)}
                              sx={{ width: 60, height: 60, mr: 2 }}
                            />
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
                                {item.productName}
                              </Typography>
                            }
                            secondary={
                                <Box>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {formatCurrency(item.unitPrice)} each
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => updateCartQuantity(item.productId, item.quantity - 1)}
                                    sx={{ border: 1, borderColor: 'divider' }}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                  <Typography variant="body1" sx={{ minWidth: 30, textAlign: 'center' }}>
                                    {item.quantity}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => updateCartQuantity(item.productId, item.quantity + 1)}
                                    disabled={!item.inStock}
                                    sx={{ border: 1, borderColor: 'divider' }}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => removeFromCart(item.productId)}
                                    sx={{ ml: 'auto', color: 'error.main' }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            }
                          />
                        </ListItem>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pr: 2, pb: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(item.unitPrice * item.quantity)}
                          </Typography>
                        </Box>
                        <Divider />
                      </React.Fragment>
                    ))}
                  </List>
                </Box>
                
                <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">Total:</Typography>
                    <Typography variant="h6" fontWeight={700} color="primary.main">
                      {formatCurrency(getCartTotal())}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handlePlaceOrder}
                    disabled={placingOrder || cart.length === 0}
                    sx={{ py: 1.5 }}
                  >
                    {placingOrder ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                        Placing Order...
                      </>
                    ) : (
                      'Place Order'
                    )}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Drawer>

        {/* Information Dialog (About Us, Shipping, Returns) */}
        <Dialog
          open={infoDialogOpen}
          onClose={handleCloseInfoDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            pb: 2,
            borderBottom: 1,
            borderColor: 'divider'
          }}>
            <Typography variant="h5" fontWeight={700}>
              {infoDialogType && getDialogContent().title}
            </Typography>
            <MuiIconButton
              aria-label="close"
              onClick={handleCloseInfoDialog}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              <CloseIcon />
            </MuiIconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <DialogContentText component="div">
              {infoDialogType && getDialogContent().content}
            </DialogContentText>
          </DialogContent>
        </Dialog>

        {/* Auth Dialog */}
        <CustomerAuthDialog 
          open={authDialogOpen} 
          onClose={() => setAuthDialogOpen(false)} 
          websitePath={websitePath}
          onLoginSuccess={handleLoginSuccess}
        />

        {/* Cart Added Notification Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={1000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{
            '& .MuiSnackbarContent-root': {
              backgroundColor: 'primary.main',
              color: 'white',
              fontWeight: 500,
              borderRadius: 2,
              minWidth: '200px'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ fontSize: 20 }} />
            <Typography variant="body1">{snackbarMessage}</Typography>
          </Box>
        </Snackbar>

        {/* Order Success Dialog */}
        <Dialog
          open={orderSuccess !== null}
          onClose={() => setOrderSuccess(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              textAlign: 'center',
              p: 4
            }
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h5" fontWeight={700} color="success.main">
              Order Placed Successfully!
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Thank you for your order. We've received your order and will process it shortly.
            </Typography>
            {orderSuccess && (
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Order Number
                </Typography>
                <Typography variant="h6" fontWeight={700} color="primary.main">
                  {orderSuccess.orderNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Total Amount: ${orderSuccess.total?.toFixed(2) || '0.00'}
                </Typography>
              </Box>
            )}
            <Button
              variant="contained"
              fullWidth
              onClick={() => setOrderSuccess(null)}
              sx={{ mt: 2 }}
            >
              Continue Shopping
            </Button>
          </DialogContent>
        </Dialog>

      </Box>
    </ThemeProvider>
  );
};

// Helper for missing icon
const NavigateNextIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Helper for missing Business icon import if needed (though imported above)
const BusinessIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
    </svg>
);

export default StorefrontTestPage;
